(function (global) {
  'use strict';

  const isPresent = (value) => String(value ?? '').trim() !== '';

  const predictiveSearchEndpoint = (value, origin) => {
    const url = new URL(value, origin);
    if (!url.pathname.endsWith('.json')) {
      url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
    }
    return url;
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = { predictiveSearchEndpoint };

  if (typeof document === 'undefined') return;

  const storefrontOrigin = new URL(document.baseURI).origin;

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = String(text);
    return element;
  };

  const safeUrl = (value, fallback = '') => {
    try {
      const url = new URL(value, storefrontOrigin);
      if (url.protocol === 'https:' || url.origin === storefrontOrigin) return url.href;
    } catch (_) {
      return fallback;
    }
    return fallback;
  };

  const productHandleFromUrl = (value) => {
    try {
      const pathname = new URL(value, storefrontOrigin).pathname;
      const match = pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  };

  const init = (root) => {
    if (root.dataset.mpMastriReady === 'true') return;
    root.dataset.mpMastriReady = 'true';

    const form = root.querySelector('[data-mp-mastr-form]');
    if (!form) return;

    const compactTool = root.dataset.layout === 'compact';
    const searchUrl = root.dataset.searchUrl || '/search/suggest';
    const rootUrl = root.dataset.rootUrl || '/';
    const stepSections = Array.from(form.querySelectorAll('[data-tool-step]'));
    const indicators = Array.from(form.querySelectorAll('[data-step-indicator]'));
    const backButton = form.querySelector('[data-step-back]');
    const nextButton = form.querySelector('[data-step-next]');
    const progressBar = form.querySelector('[data-progress-bar]');
    const status = form.querySelector('[data-tool-status]');
    const productSearch = form.querySelector('[data-product-search]');
    const searchResults = form.querySelector('[data-search-results]');
    const selectedProducts = form.querySelector('[data-selected-products]');
    const productsEmpty = form.querySelector('[data-products-empty]');
    const manualToggle = form.querySelector('[data-manual-toggle]');
    const manualBox = form.querySelector('[data-manual-product]');
    const manualTitle = form.querySelector('[data-manual-title]');
    const manualVendor = form.querySelector('[data-manual-vendor]');
    const manualAdd = form.querySelector('[data-manual-add]');
    const state = { step: 1, products: [], packet: null, packetController: null };
    let searchAbort = null;
    let searchTimer = null;

    const announce = (message, error = false) => {
      status.textContent = message || '';
      status.classList.toggle('is-error', error);
    };

    const updateStep = (nextStep, focus = true) => {
      state.step = Math.max(1, Math.min(2, nextStep));
      stepSections.forEach((section) => {
        const active = Number(section.dataset.toolStep) === state.step;
        section.hidden = !active;
        section.setAttribute('aria-hidden', String(!active));
      });
      indicators.forEach((indicator) => {
        const number = Number(indicator.dataset.stepIndicator);
        indicator.classList.toggle('is-active', number === state.step);
        indicator.classList.toggle('is-complete', number < state.step);
        if (number === state.step) indicator.setAttribute('aria-current', 'step');
        else indicator.removeAttribute('aria-current');
      });
      if (progressBar) progressBar.style.width = `${state.step * 50}%`;
      if (backButton) backButton.disabled = state.step === 1;
      if (nextButton) nextButton.hidden = state.step === 2;
      if (nextButton) nextButton.textContent = 'Zum Dokumentenpaket';
      announce('');
      if (focus) {
        const active = stepSections.find((section) => Number(section.dataset.toolStep) === state.step);
        const target = active?.querySelector('legend, h3');
        if (target) {
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
          active.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    const previewParameters = (url) => {
      const current = new URL(document.baseURI);
      ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      return url;
    };

    const fetchProductPageData = async (handle) => {
      try {
        const url = previewParameters(new URL(`${rootUrl}products/${encodeURIComponent(handle)}`, storefrontOrigin));
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const staticScript = documentFragment.querySelector('script[data-mp-product-static]');
        let specifications = [];
        if (staticScript?.textContent.trim()) {
          const parsed = JSON.parse(staticScript.textContent);
          specifications = Array.isArray(parsed.specifications) ? parsed.specifications.filter((item) => item?.name && item?.value) : [];
        }
        const seen = new Set();
        const downloads = Array.from(documentFragment.querySelectorAll('a.mp-download[href]')).reduce((items, anchor) => {
          const urlValue = safeUrl(anchor.getAttribute('href'));
          if (!urlValue || seen.has(urlValue)) return items;
          seen.add(urlValue);
          items.push({
            title: anchor.querySelector('strong')?.textContent.trim() || anchor.textContent.trim() || 'Produktdokument',
            note: anchor.textContent.replace(/\s+/g, ' ').trim(),
            url: urlValue
          });
          return items;
        }, []);
        return { specifications, downloads, fetchedAt: new Date().toISOString() };
      } catch (_) {
        return { specifications: [], downloads: [] };
      }
    };

    const fetchProductJson = async (handle) => {
      const url = new URL(`${rootUrl}products/${encodeURIComponent(handle)}.js`, storefrontOrigin);
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    };

    const roleOptions = [
      ['', 'Rolle bitte wählen'],
      ['storage', 'Speichersystem / Hauptgerät'],
      ['battery', 'Erweiterungsbatterie'],
      ['inverter', 'Wechselrichter / Mikrowechselrichter'],
      ['module', 'Solarmodul'],
      ['kit', 'Komplettset / mehrere Komponenten'],
      ['other', 'Zubehör / sonstiges Produkt']
    ];

    const productMeta = (product) => {
      const pieces = [product.vendor, product.type].filter(isPresent);
      return pieces.join(' · ') || (product.source === 'manual' ? 'Manuell eingetragen' : 'Mini Power Produkt');
    };

    const renderSelectedProducts = () => {
      selectedProducts.querySelectorAll('[data-selected-product]').forEach((item) => item.remove());
      productsEmpty.hidden = state.products.length > 0;

      state.products.forEach((product) => {
        const card = createElement('article', 'mp-mastr__product-card');
        card.dataset.selectedProduct = product.key;
        const media = product.image
          ? Object.assign(createElement('img'), { src: product.image, alt: product.title, loading: 'lazy', width: 96, height: 96 })
          : createElement('div', 'mp-mastr__product-placeholder', 'Produkt');
        card.appendChild(media);

        const body = createElement('div');
        const head = createElement('div', 'mp-mastr__product-head');
        const titleWrap = createElement('div');
        titleWrap.append(createElement('strong', '', product.title), createElement('small', '', productMeta(product)));
        const remove = createElement('button', 'mp-mastr__remove', 'Entfernen');
        remove.type = 'button';
        remove.addEventListener('click', () => {
          state.products = state.products.filter((item) => item.key !== product.key);
          invalidatePacket();
          renderSelectedProducts();
          announce(`${product.title} wurde entfernt.`);
        });
        head.append(titleWrap, remove);
        body.appendChild(head);

        const controls = createElement('div', 'mp-mastr__product-controls');
        const roleLabel = createElement('label', '', 'Produkttyp');
        const roleSelect = createElement('select');
        roleOptions.forEach(([optionValue, label]) => {
          const option = createElement('option', '', label);
          option.value = optionValue;
          option.selected = product.role === optionValue;
          roleSelect.appendChild(option);
        });
        roleSelect.addEventListener('change', () => {
          product.role = roleSelect.value;
          invalidatePacket();
          renderSelectedProducts();
          announce(product.role ? 'Produkttyp übernommen.' : 'Bitte wählen Sie die Produktrolle.');
        });
        roleLabel.appendChild(roleSelect);
        if (!compactTool && (!product.role || product.source === 'manual')) controls.appendChild(roleLabel);

        const variants = Array.isArray(product.variants) ? product.variants : [];
        const variantLabel = createElement('label', '', 'Variante / SKU');
        if (variants.length > 1 || (variants[0] && variants[0].title !== 'Default Title')) {
          const variantSelect = createElement('select');
          variantSelect.appendChild(Object.assign(createElement('option', '', 'Gekaufte Variante wählen'), { value: '' }));
          variants.forEach((variant) => {
            const label = [variant.title, variant.sku].filter(isPresent).join(' · ') || `Variante ${variant.id}`;
            const option = createElement('option', '', label);
            option.value = String(variant.id);
            option.selected = String(product.variantId || '') === String(variant.id);
            variantSelect.appendChild(option);
          });
          variantSelect.addEventListener('change', () => {
            product.variantId = variantSelect.value;
            const selected = variants.find((variant) => String(variant.id) === variantSelect.value);
            product.variantTitle = selected?.title || '';
            product.sku = selected?.sku || '';
            product.variantSelected = !!selected;
            invalidatePacket();
            renderSelectedProducts();
          });
          variantLabel.appendChild(variantSelect);
        } else {
          const variantText = createElement('input');
          variantText.type = 'text';
          variantText.value = product.sku || variants[0]?.sku || 'Standardvariante';
          variantText.readOnly = true;
          variantLabel.appendChild(variantText);
        }
        if (!compactTool || variants.length > 1) controls.appendChild(variantLabel);

        body.appendChild(controls);
        const modelNote = global.MPMaStRPack?.getNote(product);
        if (modelNote && !compactTool) body.appendChild(createElement('p', 'mp-mastr__field-help', modelNote));

        if (product.specifications.length && !compactTool) {
          const details = createElement('details', 'mp-mastr__product-details');
          details.appendChild(createElement('summary', '', `${product.specifications.length} aktuell hinterlegte Spezifikationen anzeigen`));
          const list = createElement('dl');
          product.specifications.forEach((specification) => {
            list.append(createElement('dt', '', specification.name), createElement('dd', '', specification.value));
          });
          details.appendChild(list);
          body.appendChild(details);
        }

        const links = createElement('div', 'mp-mastr__product-links');
        if (product.url) {
          const productLink = createElement('a', '', 'Produktseite öffnen');
          productLink.href = product.url;
          productLink.target = '_blank';
          productLink.rel = 'noopener noreferrer';
          links.appendChild(productLink);
        }
        product.downloads.forEach((download) => {
          const link = createElement('a', '', download.title);
          link.href = download.url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          links.appendChild(link);
        });
        if (links.children.length && !compactTool) body.appendChild(links);
        card.appendChild(body);
        selectedProducts.appendChild(card);
      });
      if (compactTool) {
        form.querySelector('[data-create-pack]').disabled = !state.products.length || state.products.some(p => !p.variantId);
        renderPackPreview();
      }
    };

    const addProduct = async (suggestion) => {
      const handle = suggestion.handle || productHandleFromUrl(suggestion.url);
      if (!handle) {
        announce('Dieses Suchergebnis konnte keinem eindeutigen Produkt-Handle zugeordnet werden.', true);
        return;
      }
      if (state.products.some((product) => product.handle === handle)) {
        announce('Dieses Produkt ist bereits ausgewählt. Sie können weitere Produkte hinzufügen.');
        return;
      }

      announce('Produktdaten und Dokumente werden geladen …');
      try {
        const [productJson, pageData] = await Promise.all([
          fetchProductJson(handle),
          fetchProductPageData(handle)
        ]);
        const variants = Array.isArray(productJson.variants) ? productJson.variants : [];
        const firstVariant = variants.length === 1 ? variants[0] : {};
        const modelTitle = String(productJson.title || '').normalize('NFKC').replace(/[\u00ad\u200b]/g, '');
        const product = {
          key: `shop-${productJson.id || handle}`,
          source: 'shop',
          id: productJson.id || '',
          handle,
          title: productJson.title || suggestion.title || handle,
          vendor: productJson.vendor || suggestion.vendor || '',
          type: productJson.type || suggestion.type || '',
          tags: Array.isArray(productJson.tags) ? productJson.tags : [],
          image: safeUrl(productJson.featured_image || suggestion.image || '', ''),
          url: safeUrl(`${rootUrl}products/${handle}`, `${rootUrl}products/${handle}`),
          variants,
          variantId: firstVariant.id || '',
          variantTitle: firstVariant.title || '',
          sku: firstVariant.sku || '',
          fetchedAt: pageData.fetchedAt || new Date().toISOString(),
          specifications: pageData.specifications,
          downloads: pageData.downloads,
          role: /Erweiterungs|Zusatzbatterie/i.test(modelTitle) ? 'battery' : /Solarmodul.*(Set|mit)|Set.*Solarmodul|Komplettset/i.test(modelTitle) ? 'kit' : /Mikrowechselrichter|Wechselrichter/i.test(modelTitle) ? 'inverter' : /Speicher|Solarbank|SolarFlow|STREAM/i.test(modelTitle) ? 'storage' : /Solarmodul|Solarpanel/i.test(modelTitle) ? 'module' : '',
          variantSelected: !!firstVariant.id,
          quantity: 1
        };
        state.products.push(product);
        invalidatePacket();
        renderSelectedProducts();
        announce(compactTool ? 'Produkt hinzugefügt.' : `${product.title} wurde hinzugefügt. Sie können weitere Produkte wählen oder das Dokumentenpaket herunterladen.`);
      } catch (_) {
        announce('Die aktuellen Produktdaten konnten nicht vollständig geladen werden. Bitte versuchen Sie es erneut.', true);
      }
    };

    const renderSearchResults = (products) => {
      searchResults.replaceChildren();
      if (!products.length) {
        const empty = createElement('p', 'mp-mastr__empty', 'Kein eindeutiges Produkt gefunden. Bitte prüfen Sie die Schreibweise.');
        searchResults.appendChild(empty);
        searchResults.hidden = false;
        return;
      }
      products.forEach((product) => {
        const handle = product.handle || productHandleFromUrl(product.url);
        const button = createElement('button', 'mp-mastr__search-result');
        button.type = 'button';
        button.setAttribute('role', 'option');
        const imageUrl = safeUrl(product.image?.url || product.image || '', '');
        if (imageUrl) {
          const image = createElement('img');
          image.src = imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 54;
          image.height = 54;
          button.appendChild(image);
        } else {
          button.appendChild(createElement('span', '', ''));
        }
        const copy = createElement('span');
        copy.append(createElement('strong', '', product.title || handle), createElement('small', '', [product.vendor, product.type].filter(isPresent).join(' · ')));
        button.append(copy, createElement('span', '', 'Auswählen'));
        button.addEventListener('click', () => {
          searchResults.hidden = true;
          productSearch.value = '';
          addProduct({ ...product, handle, image: imageUrl });
        });
        searchResults.appendChild(button);
      });
      searchResults.hidden = false;
    };

    const searchProducts = async (query) => {
      if (searchAbort) searchAbort.abort();
      searchAbort = new AbortController();
      try {
        const url = predictiveSearchEndpoint(searchUrl, storefrontOrigin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('resources[options][unavailable_products]', 'show');
        url.searchParams.set('resources[options][fields]', 'title,product_type,vendor,variants.title,variants.sku');
        const response = await fetch(url, {
          signal: searchAbort.signal,
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        renderSearchResults(data?.resources?.results?.products || []);
      } catch (error) {
        if (error.name === 'AbortError') return;
        searchResults.hidden = true;
        announce('Die Produktsuche ist gerade nicht erreichbar. Bitte versuchen Sie es erneut.', true);
      }
    };

    const packStatus = form.querySelector('[data-pack-status]');
    const invalidatePacket = () => {
      state.packet = null;
      if (state.packetController) state.packetController.abort();
      form.querySelectorAll('[data-pack-pdf]').forEach(button => button.disabled = true);
      if (packStatus) packStatus.textContent = compactTool ? '' : 'Auswahl geändert. Paket für die aktuelle Produktauswahl neu erstellen.';
    };
    const packSnapshot = () => ({ origin: storefrontOrigin, products: JSON.parse(JSON.stringify(state.products)) });
    const renderPackPreview = () => {
      const host = form.querySelector('[data-pack-preview]');
      if (!host || !global.MPMaStRPack) return;
      if (compactTool && !state.products.length) { host.replaceChildren(); return; }
      const plan = global.MPMaStRPack.plan(packSnapshot());
      host.replaceChildren();
      if (compactTool) host.append(createElement('p', '', plan.docs.length + ' Hersteller-PDFs + Registrierungsanleitung + fehlende Unterlagen'));
      else host.append(createElement('strong', '', plan.docs.length + ' Hersteller-PDF(s) + 2 Hilfs-PDFs'),
        createElement('p', '', 'Originale Datenblätter, Zertifikate und weitere verfügbare Unterlagen, ergänzt um Registrierungsanleitung und fehlende Dokumente mit Bezugswegen. ' + plan.gaps.length + ' Prüf- und Beschaffungspunkte sind offen.'));
      const details = createElement('details');
      details.appendChild(createElement('summary', '', 'Vorgesehene Dateien und offene Punkte ansehen'));
      const list = createElement('ul');
      plan.docs.forEach(d => list.appendChild(createElement('li', '', d.title)));
      plan.gaps.forEach(g => list.appendChild(createElement('li', '', g.group + ': ' + g.title)));
      details.appendChild(list); host.appendChild(details);
    };
    const createPack = async () => {
      if (!global.MPMaStRPack || state.packetController) return;
      if (!checkSelection()) return;
      renderPackPreview();
      const controller = new AbortController(); state.packetController = controller;
      const start = form.querySelector('[data-create-pack]'), cancel = form.querySelector('[data-cancel-pack]');
      start.disabled = true; cancel.hidden = false;
      form.querySelectorAll('[data-pack-pdf]').forEach(b => b.disabled = true);
      try {
        const packet = await global.MPMaStRPack.generate(packSnapshot(), {
          signal: controller.signal,
          progress: (done, total, title) => { packStatus.textContent = done + ' / ' + total + ' Originaldateien: ' + title; }
        });
        state.packet = packet;
        const included = packet.result.docs.filter(d => d.status === 'included').length;
        const failed = packet.result.docs.filter(d => d.status === 'unavailable').length;
        packStatus.textContent = 'Paket erstellt: 2 Hilfs-PDFs und ' + included + ' Originaldateien. ' + packet.result.gaps.length + ' Prüf-/Beschaffungspunkte stehen in PDF 03.' + (failed ? ' ' + failed + ' Datei(en) konnten nicht beigefügt werden; Direktlinks stehen in PDF 03.' : '');
        form.querySelectorAll('[data-pack-pdf]').forEach(b => b.disabled = false);
        global.MPMaStRPack.download(packet.blob, 'Mini-Power-Registrierungspaket-' + new Date().toISOString().slice(0, 10) + '.zip');
      } catch (error) {
        packStatus.textContent = controller.signal.aborted ? 'Erstellung abgebrochen. Sie können mit der aktuellen Auswahl neu starten.' : 'Paket konnte nicht erstellt werden. Bitte erneut versuchen; die Produktlinks bleiben verfügbar.';
      } finally { state.packetController = null; start.disabled = false; cancel.hidden = true; }
    };
    form.addEventListener('input', () => invalidatePacket());
    form.addEventListener('change', () => invalidatePacket());
    form.querySelector('[data-create-pack]')?.addEventListener('click', createPack);
    form.querySelector('[data-cancel-pack]')?.addEventListener('click', () => state.packetController?.abort());
    form.querySelectorAll('[data-pack-pdf]').forEach(button => button.addEventListener('click', () => {
      if (!state.packet) return;
      const key = button.dataset.packPdf;
      const names = { guide: 'Registrierungsanleitung.pdf', missing: 'Fehlende-Unterlagen.pdf' };
      global.MPMaStRPack.download(state.packet[key], names[key], 'application/pdf');
    }));

    const checkSelection = () => {
      if (!state.products.length) { announce('Bitte wählen Sie zuerst ein Produkt.', true); productSearch.focus(); return false; }
      if (state.products.some(p => p.source === 'shop' && !p.variantId)) { announce('Bitte wählen Sie die passende Produktvariante.', true); return false; }
      return true;
    };

    productSearch.addEventListener('input', () => {
      global.clearTimeout(searchTimer);
      const query = productSearch.value.trim();
      if (query.length < 2) {
        searchResults.hidden = true;
        searchResults.replaceChildren();
        return;
      }
      searchTimer = global.setTimeout(() => searchProducts(query), 260);
    });

    productSearch.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') searchResults.hidden = true;
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.mp-mastr__product-search')) searchResults.hidden = true;
    });

    form.addEventListener('submit', (event) => event.preventDefault());

    manualToggle?.addEventListener('click', () => {
      manualBox.hidden = !manualBox.hidden;
      manualToggle.setAttribute('aria-expanded', String(!manualBox.hidden));
      if (!manualBox.hidden) manualTitle.focus();
    });

    manualAdd?.addEventListener('click', () => {
      const title = manualTitle.value.trim();
      if (!title) {
        announce('Bitte geben Sie den exakten Produktnamen ein.', true);
        manualTitle.focus();
        return;
      }
      const product = {
        key: `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        source: 'manual',
        handle: '',
        title,
        vendor: manualVendor.value.trim(),
        type: '',
        tags: [],
        image: '',
        url: '',
        variants: [],
        variantId: '',
        variantTitle: '',
        sku: '',
        specifications: [],
        downloads: [],
        role: '',
        quantity: 1
      };
      state.products.push(product);
      manualTitle.value = '';
      manualVendor.value = '';
      invalidatePacket();
      renderSelectedProducts();
      announce('Das Produkt wurde manuell hinzugefügt. Bitte wählen Sie seine Rolle und prüfen Sie alle Werte in den Originalunterlagen.');
    });

    backButton?.addEventListener('click', () => updateStep(1));
    nextButton?.addEventListener('click', () => {
      if (!checkSelection()) return;
      renderPackPreview();
      updateStep(2);
    });

    if (!compactTool) updateStep(1, false);
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-mp-mastr-root]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})(typeof window !== 'undefined' ? window : globalThis);

